import dayjs from "dayjs";
import "dayjs/locale/zh-cn";
import "dayjs/locale/en";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

type Language = "zh-cn" | "en";

interface DateFormat {
  yesterday: string;
  sameYear: string;
  differentYear: string;
}

const DATE_FORMATS: Record<Language, DateFormat> = {
  "zh-cn": {
    yesterday: "昨天 HH:mm",
    sameYear: "MM-DD HH:mm",
    differentYear: "YYYY-MM-DD",
  },
  en: {
    yesterday: "[Yesterday] HH:mm",
    sameYear: "MMM D, HH:mm",
    differentYear: "MMM D, YYYY",
  },
};

/**
 * 格式化时间戳为人性化的日期显示
 *
 * 规则：
 * 1. 今天：显示相对时间（几分钟前，几小时前）
 * 2. 昨天：显示"昨天 HH:mm"（中文）或"Yesterday HH:mm"（英文）
 * 3. 本年：显示"MM-DD HH:mm"（中文）或"MMM D, HH:mm"（英文）
 * 4. 往年：显示"YYYY-MM-DD"（中文）或"MMM D, YYYY"（英文）
 *
 * @param {number} timestamp - Unix 时间戳（秒）
 * @param {Language} [lang="zh-cn"] - 语言选项："zh-cn" | "en"
 * @returns {string} 格式化后的日期字符串
 *
 * @example
 * // 中文示例
 * formatDateByDayJs(Date.now() / 1000) // "几秒前"
 * formatDateByDayJs(Date.now() / 1000 - 86400) // "昨天 14:30"
 *
 * // 英文示例
 * formatDateByDayJs(Date.now() / 1000, "en") // "a few seconds ago"
 * formatDateByDayJs(Date.now() / 1000 - 86400, "en") // "Yesterday 14:30"
 */
const formatDateByDayJs = (
  timestamp: number,
  lang: Language = "zh-cn"
): string => {
  dayjs.locale(lang);

  const date = dayjs(timestamp * 1000);
  const now = dayjs();
  const diffDays = now.diff(date, "day");
  const format = DATE_FORMATS[lang];

  // 今天：显示相对时间
  if (diffDays === 0) return date.fromNow();

  // 昨天：根据语言显示格式
  if (diffDays === 1) return date.format(format.yesterday);

  // 本年：根据语言显示格式
  if (date.year() === now.year()) return date.format(format.sameYear);

  // 往年：根据语言显示格式
  return date.format(format.differentYear);
};

export { formatDateByDayJs, type Language };
